// Theme configuration based on MUI v7 ThemeProvider
// See: https://mui.com/material-ui/customization/theming/
import React from "react";
import PropTypes from "prop-types";
import { ThemeProvider, createTheme, darken } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { blue, pink } from "@mui/material/colors";
import { getCookie } from "../utils/helpers";

export const themeInitialOptions = {
  direction: "ltr",
  paletteColors: {
    primary: {
      main: blue[500]
    },
    secondary: {
      // Darken so we reach the AA contrast ratio level.
      main: darken(pink.A400, 0.08)
    }
  }
};

export const DispatchContext = React.createContext(() => {
  throw new Error("Forgot to wrap component in ThemeContext.Provider");
});

const useEnhancedEffect =
  typeof window === "undefined" ? React.useEffect : React.useLayoutEffect;

export function Provider(props) {
  const { children } = props;

  const [themeOptions, dispatch] = React.useReducer((state, action) => {
    switch (action.type) {
      case "RESET_COLORS":
        return {
          ...state,
          paletteColors: themeInitialOptions.paletteColors
        };
      case "CHANGE":
        return {
          paletteType: action.payload.paletteType || state.paletteType,
          direction: action.payload.direction || state.direction,
          paletteColors: action.payload.paletteColors || state.paletteColors
        };
      default:
        throw new Error(`Unrecognized type ${action.type}`);
    }
  }, themeInitialOptions);

  const prefersDarkMode = useMediaQuery("@media (prefers-color-scheme: dark)");
  const preferredType = prefersDarkMode ? "dark" : "light";
  const {
    direction,
    paletteColors,
    paletteType = preferredType
  } = themeOptions;

  React.useEffect(() => {
    if (process.browser) {
      const nextPaletteColors = JSON.parse(
        getCookie("paletteColors") || "null"
      );
      const nextPaletteType = getCookie("paletteType");

      dispatch({
        type: "CHANGE",
        payload: {
          paletteColors: nextPaletteColors,
          paletteType: nextPaletteType
        }
      });
    }
  }, []);

  // persist paletteType
  React.useEffect(() => {
    document.cookie = `paletteType=${paletteType};path=/;max-age=31536000`;
  }, [paletteType]);

  useEnhancedEffect(() => {
    document.body.dir = direction;
  }, [direction]);

  const theme = React.useMemo(() => {
    const nextTheme = createTheme({
      direction,
      nprogress: {
        color: paletteType === "light" ? "#000" : "#fff"
      },
      palette: {
        ...paletteColors,
        mode: paletteType,
        background: {
          default: paletteType === "light" ? "#fff" : "#303030"
        },
        // Industrial Precision theme - accent colors
        accent: {
          main: paletteType === "light" ? "#F59E0B" : "#FBBF24",
          light: paletteType === "light" ? "#FCD34D" : "#FDE68A",
          dark: paletteType === "light" ? "#D97706" : "#F59E0B",
          contrastText: "#000"
        },
        // Status indicator colors
        status: {
          live: paletteType === "light" ? "#10B981" : "#34D399",
          notLive: paletteType === "light" ? "#6B7280" : "#9CA3AF",
          modified: paletteType === "light" ? "#F59E0B" : "#FBBF24",
          inUse: paletteType === "light" ? "#3B82F6" : "#60A5FA"
        },
        // Surface colors for cards and elevated elements
        surface: {
          card: paletteType === "light" ? "#FFFFFF" : "#1E293B",
          elevated: paletteType === "light" ? "#F8FAFC" : "#334155",
          border: paletteType === "light" ? "#E2E8F0" : "#475569"
        }
      }
    });

    nextTheme.palette.background.level1 =
      paletteType === "light"
        ? nextTheme.palette.grey[100]
        : nextTheme.palette.grey[900];

    nextTheme.palette.background.level0 =
      paletteType === "light"
        ? nextTheme.palette.grey[50]
        : nextTheme.palette.grey[900];

    return nextTheme;
  }, [direction, paletteColors, paletteType]);

  React.useEffect(() => {
    // Expose the theme as a global variable so people can play with it.
    if (process.browser) {
      window.theme = theme;
    }
  }, [theme]);

  // StyledEngineProvider is in index.tsx - only need ThemeProvider here
  return (
    <ThemeProvider theme={theme}>
      <DispatchContext.Provider value={dispatch}>
        {children}
      </DispatchContext.Provider>
    </ThemeProvider>
  );
}

Provider.propTypes = {
  children: PropTypes.node
};

export function useChangeTheme() {
  const dispatch = React.useContext(DispatchContext);
  return React.useCallback(
    options => dispatch({ type: "CHANGE", payload: options }),
    [dispatch]
  );
}
